/**
 * /admin/annuaire — Gestion complète des moto-écoles (Phase 34.G).
 *
 * Fonctionnalités :
 *  - Recherche + filtres (dept, source, hidden)
 *  - Édition inline (dialog) de toutes les infos publiques
 *  - Suppression : soft (opposition_status='removed') ou hard (irréversible)
 *  - Restauration d'une fiche masquée
 *  - Consultation des demandes RGPD (claims, oppositions, signalements)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Search, MapPin, Trash2, Edit3, RefreshCw, ShieldCheck, AlertTriangle,
  Eye, EyeOff, Building2, Upload, FileSpreadsheet, ChevronLeft, ChevronRight,
  ShieldAlert, Award, Users, Megaphone
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const tok = localStorage.getItem('token');
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

async function apiGet(path) {
  const r = await fetch(`${API}${path}`, { headers: { ...authHeaders() } });
  if (!r.ok) throw new Error((await r.json()).detail || r.statusText);
  return r.json();
}
async function apiJson(path, method, body) {
  const r = await fetch(`${API}${path}`, {
    method, headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: body ? JSON.stringify(body) : undefined,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.detail || r.statusText);
  return j;
}
async function apiUpload(path, file) {
  const fd = new FormData();
  fd.append('file', file);
  const r = await fetch(`${API}${path}`, { method: 'POST', headers: { ...authHeaders() }, body: fd });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.detail || r.statusText);
  return j;
}

export default function AdminAnnuaire() {
  return (
    <div className="space-y-5 max-w-6xl" data-testid="admin-annuaire-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-1">
            <Building2 className="w-3.5 h-3.5" /> Back-office admin
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Annuaire moto-écoles</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Modifier, supprimer, restaurer les fiches et gérer les demandes RGPD (revendications, oppositions, signalements).
          </p>
        </div>
        <Button asChild variant="outline" size="sm" className="border-amber-500/40 text-amber-600 dark:text-amber-300 hover:bg-amber-500/10 font-black" data-testid="btn-export-pack-annuaire">
          <Link to="/admin/annuaire/export" title="Télécharger le pack d'intégration (admin only)">
            🔗 Pack d&apos;intégration (ZIP)
          </Link>
        </Button>
      </div>

      <Tabs defaultValue="schools" className="w-full">
        <TabsList className="grid grid-cols-4 max-w-2xl">
          <TabsTrigger value="schools" data-testid="tab-schools">Fiches</TabsTrigger>
          <TabsTrigger value="claims" data-testid="tab-claims">Revendications</TabsTrigger>
          <TabsTrigger value="oppositions" data-testid="tab-oppositions">Oppositions</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">Signalements</TabsTrigger>
        </TabsList>
        <TabsContent value="schools" className="mt-4"><SchoolsTab /></TabsContent>
        <TabsContent value="claims" className="mt-4"><ClaimsTab /></TabsContent>
        <TabsContent value="oppositions" className="mt-4"><OppositionsTab /></TabsContent>
        <TabsContent value="reports" className="mt-4"><ReportsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ═══════════════ Tab: Fiches ═══════════════ */

function SchoolsTab() {
  const [q, setQ] = useState('');
  const [dept, setDept] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [includeHidden, setIncludeHidden] = useState(true);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 0 });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '50', include_hidden: String(includeHidden) });
      if (q.trim()) params.set('q', q.trim());
      if (dept) params.set('dept', dept);
      if (sourceType) params.set('source_type', sourceType);
      const j = await apiGet(`/api/admin/annuaire/schools?${params}`);
      setData(j);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [q, dept, sourceType, includeHidden, page]);

  useEffect(() => { load(); }, [load]);

  const del = async (school, hard = false) => {
    try {
      const q2 = hard ? '?hard=true' : '';
      await apiJson(`/api/admin/annuaire/schools/${school.id}${q2}`, 'DELETE');
      toast.success(hard ? 'Fiche supprimée définitivement.' : 'Fiche masquée (soft-delete).');
      setDeleting(null);
      load();
    } catch (e) { toast.error(e.message); }
  };
  const restore = async (school) => {
    try {
      await apiJson(`/api/annuaire/admin/school/${school.id}/opposition/restore`, 'POST');
      toast.success('Fiche restaurée.');
      load();
    } catch (e) { toast.error(e.message); }
  };
  const setRank = async (school, rank) => {
    try {
      const body = rank == null ? { featured_rank: null, featured_partner: null } : { featured_rank: rank };
      await apiJson(`/api/admin/annuaire/schools/${school.id}`, 'PATCH', body);
      toast.success(rank == null ? 'Retiré de la mise en avant.' : `Épinglée en rang ${rank}.`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="rounded-2xl border bg-card p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <Label className="text-[11px] font-black uppercase tracking-widest">Recherche</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={q} onChange={e => { setQ(e.target.value); setPage(1); }} placeholder="Nom, ville, CP, ID…" className="pl-9" data-testid="admin-annuaire-search" />
            </div>
          </div>
          <div>
            <Label className="text-[11px] font-black uppercase tracking-widest">Département</Label>
            <Input value={dept} onChange={e => { setDept(e.target.value); setPage(1); }} placeholder="ex. 75" className="mt-1" data-testid="admin-annuaire-dept" />
          </div>
          <div>
            <Label className="text-[11px] font-black uppercase tracking-widest">Source</Label>
            <select value={sourceType} onChange={e => { setSourceType(e.target.value); setPage(1); }} className="mt-1 w-full h-10 px-3 rounded-md border bg-background text-sm" data-testid="admin-annuaire-source">
              <option value="">Toutes</option>
              <option value="data_gouv">data.gouv.fr</option>
              <option value="editorial">Éditoriales</option>
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={includeHidden} onCheckedChange={setIncludeHidden} data-testid="admin-annuaire-include-hidden" />
            <span>Inclure les fiches masquées (opposition RGPD)</span>
          </label>
          <div className="flex gap-2">
            <SyncDataGouvButton onDone={load} />
            <BulkFeatureButton onDone={load} />
            <Link to="/admin/annuaire/partenaires" data-testid="admin-annuaire-partners-link">
              <Button variant="outline" size="sm" className="border-amber-500/50 text-amber-700 hover:bg-amber-500/10 hover:text-amber-800 dark:text-amber-300 dark:hover:text-amber-200">
                <Megaphone className="w-3.5 h-3.5 mr-1.5" /> Bannière partenaires
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)} data-testid="admin-annuaire-create-btn">
              + Nouvelle fiche
            </Button>
            <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="admin-annuaire-import-btn">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Import XLSX
            </Button>
            <Button variant="ghost" size="sm" onClick={load} data-testid="admin-annuaire-reload"><RefreshCw className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
        <div className="text-[12px] text-muted-foreground">
          {loading ? 'Chargement…' : `${data.total.toLocaleString('fr-FR')} fiche${data.total > 1 ? 's' : ''} trouvée${data.total > 1 ? 's' : ''} · page ${page}/${data.pages || 1}`}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-[11px] font-black uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Nom</th>
                <th className="text-left p-3">Ville</th>
                <th className="text-left p-3">Dept</th>
                <th className="text-left p-3">Permis</th>
                <th className="text-left p-3">Rang</th>
                <th className="text-left p-3">Source</th>
                <th className="text-left p-3">Statut</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(s => (
                <tr key={s.id} className="border-t hover:bg-muted/20" data-testid={`admin-school-row-${s.id}`}>
                  <td className="p-3">
                    <div className="font-bold">{s.nom}</div>
                    <div className="text-[10.5px] text-muted-foreground">{s.id}</div>
                  </td>
                  <td className="p-3">{s.ville} <span className="text-muted-foreground text-[11px]">{s.code_postal}</span></td>
                  <td className="p-3 font-mono text-[11px]">{s.departement}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {(s.permis || []).map(p => <span key={p} className="px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-bold">{p}</span>)}
                    </div>
                  </td>
                  <td className="p-3">
                    {s.featured_rank != null ? (
                      <div className="flex items-center gap-1">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 text-[10px] font-black" title={s.featured_partner || 'Featured'}>
                          <Award className="w-3 h-3" /> #{s.featured_rank}
                        </span>
                        <button type="button" onClick={() => setRank(s, s.featured_rank + 1)} title="Descendre" className="hover:text-primary p-0.5" data-testid={`admin-rank-down-${s.id}`}>▼</button>
                        <button type="button" onClick={() => setRank(s, Math.max(1, s.featured_rank - 1))} title="Monter" className="hover:text-primary p-0.5" data-testid={`admin-rank-up-${s.id}`}>▲</button>
                        <button type="button" onClick={() => setRank(s, null)} title="Retirer la mise en avant" className="hover:text-destructive p-0.5 text-muted-foreground" data-testid={`admin-rank-clear-${s.id}`}>✕</button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => setRank(s, 1)} className="text-[10.5px] text-muted-foreground hover:text-amber-600 underline" data-testid={`admin-rank-pin-${s.id}`}>
                        Épingler
                      </button>
                    )}
                  </td>
                  <td className="p-3 text-[11px]">
                    {s.source_type === 'editorial' ? (
                      <span className="inline-flex items-center gap-1 text-amber-600"><AlertTriangle className="w-3 h-3" /> Éditorial</span>
                    ) : s.source_type === 'admin' ? (
                      <span className="inline-flex items-center gap-1 text-blue-600">Manuel</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-emerald-600"><ShieldCheck className="w-3 h-3" /> data.gouv</span>
                    )}
                  </td>
                  <td className="p-3 text-[11px]">
                    {s.opposition_status === 'hidden' && <span className="inline-flex items-center gap-1 text-orange-600"><EyeOff className="w-3 h-3" /> Masquée</span>}
                    {s.opposition_status === 'removed' && <span className="inline-flex items-center gap-1 text-red-600"><Trash2 className="w-3 h-3" /> Retirée</span>}
                    {(!s.opposition_status || s.opposition_status === 'active') && (
                      s.claim_status === 'verifiee' ? <span className="inline-flex items-center gap-1 text-blue-600"><ShieldCheck className="w-3 h-3" /> Vérifiée</span>
                      : <span className="text-muted-foreground">Publique</span>
                    )}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {(s.opposition_status === 'hidden' || s.opposition_status === 'removed') && (
                      <Button size="sm" variant="ghost" onClick={() => restore(s)} title="Restaurer" data-testid={`admin-school-restore-${s.id}`}>
                        <Eye className="w-3.5 h-3.5 text-emerald-600" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setEditing(s)} title="Modifier" data-testid={`admin-school-edit-${s.id}`}>
                      <Edit3 className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeleting(s)} title="Supprimer" data-testid={`admin-school-delete-${s.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
              {data.items.length === 0 && !loading && (
                <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">Aucune fiche trouvée.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
            <ChevronLeft className="w-3.5 h-3.5" /> Précédent
          </Button>
          <span className="text-sm text-muted-foreground px-3">Page {page} / {data.pages}</span>
          <Button size="sm" variant="outline" onClick={() => setPage(p => Math.min(data.pages, p + 1))} disabled={page >= data.pages}>
            Suivant <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}

      {/* Dialog édition */}
      {editing && <EditSchoolDialog school={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {/* Dialog création */}
      {createOpen && <CreateSchoolDialog onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />}
      {/* Dialog import XLSX */}
      {importOpen && <ImportXlsxDialog onClose={() => setImportOpen(false)} onImported={() => { setImportOpen(false); load(); }} />}
      {/* Dialog suppression */}
      {deleting && (
        <AlertDialog open={true} onOpenChange={() => setDeleting(null)}>
          <AlertDialogContent data-testid="admin-annuaire-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-destructive" /> Supprimer {deleting.nom} ?</AlertDialogTitle>
              <AlertDialogDescription>
                <strong className="text-foreground">Suppression douce (recommandé)</strong> : la fiche sera masquée (opposition_status="removed") mais restaurable.<br />
                <strong className="text-destructive">Suppression définitive</strong> : la fiche sera <em>effacée de la base</em>, action irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-2">
              <AlertDialogCancel data-testid="admin-annuaire-delete-cancel">Annuler</AlertDialogCancel>
              <Button variant="outline" onClick={() => del(deleting, false)} data-testid="admin-annuaire-delete-soft">
                Masquer (soft)
              </Button>
              <AlertDialogAction onClick={() => del(deleting, true)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="admin-annuaire-delete-hard">
                Supprimer définitivement
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

/* ═══════════════ Dialog: édition école ═══════════════ */

function EditSchoolDialog({ school, onClose, onSaved }) {
  const [form, setForm] = useState({ ...school, permis_str: (school.permis || []).join(',') });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const permis = form.permis_str.split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
      const body = {
        nom: form.nom, adresse: form.adresse, code_postal: form.code_postal, ville: form.ville,
        site_internet: form.site_internet || null,
        permis,
        label_qualite: !!form.label_qualite,
        custom_email: form.custom_email || null,
        custom_phone: form.custom_phone || null,
        custom_website: form.custom_website || null,
        custom_description: form.custom_description || null,
        featured_rank: form.featured_rank === '' || form.featured_rank == null ? null : Number(form.featured_rank),
        featured_partner: form.featured_partner || null,
      };
      await apiJson(`/api/admin/annuaire/schools/${school.id}`, 'PATCH', body);
      toast.success('Fiche mise à jour.');
      onSaved();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-annuaire-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Edit3 className="w-5 h-5" /> Modifier la fiche</DialogTitle>
          <DialogDescription className="font-mono text-[11px]">ID : {school.id}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <FormRow label="Nom" v={form.nom} onChange={v => setForm({ ...form, nom: v })} testid="edit-nom" />
          <FormRow label="Adresse" v={form.adresse} onChange={v => setForm({ ...form, adresse: v })} testid="edit-adresse" />
          <div className="grid grid-cols-3 gap-3">
            <FormRow label="CP" v={form.code_postal} onChange={v => setForm({ ...form, code_postal: v })} testid="edit-cp" />
            <FormRow label="Ville" v={form.ville} onChange={v => setForm({ ...form, ville: v })} testid="edit-ville" className="col-span-2" />
          </div>
          <FormRow label="Permis (A,A1,A2,AM séparés par virgule)" v={form.permis_str} onChange={v => setForm({ ...form, permis_str: v })} testid="edit-permis" />
          <FormRow label="Site internet" v={form.site_internet || ''} onChange={v => setForm({ ...form, site_internet: v })} testid="edit-site" />
          <div className="pt-2 border-t">
            <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground mb-2">Personnalisation (fiche revendiquée / éditoriale)</div>
            <FormRow label="Téléphone pro" v={form.custom_phone || ''} onChange={v => setForm({ ...form, custom_phone: v })} testid="edit-phone" />
            <FormRow label="Email pro" v={form.custom_email || ''} onChange={v => setForm({ ...form, custom_email: v })} testid="edit-email" />
            <FormRow label="Site (custom)" v={form.custom_website || ''} onChange={v => setForm({ ...form, custom_website: v })} testid="edit-custom-web" />
            <div className="mt-2">
              <Label className="text-[11px] font-black uppercase tracking-widest">Description</Label>
              <textarea value={form.custom_description || ''} onChange={e => setForm({ ...form, custom_description: e.target.value })} rows={3} className="mt-1 w-full p-2 rounded-md border bg-background text-sm" data-testid="edit-desc" />
            </div>
          </div>
          <label className="flex items-center gap-2 pt-2">
            <Checkbox checked={!!form.label_qualite} onCheckedChange={v => setForm({ ...form, label_qualite: !!v })} data-testid="edit-label" />
            <span className="text-sm">Label Qualité des Formations aux Métiers de la Route</span>
          </label>

          {/* Mise en avant (partenaires) */}
          <div className="pt-3 border-t space-y-2">
            <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-muted-foreground">
              <Award className="w-3.5 h-3.5 text-amber-500" /> Mise en avant (partenaires)
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[11px] font-black uppercase tracking-widest">Rang</Label>
                <Input
                  type="number" min="1" max="9999"
                  value={form.featured_rank ?? ''}
                  onChange={e => setForm({ ...form, featured_rank: e.target.value === '' ? null : Number(e.target.value) })}
                  placeholder="—"
                  className="mt-1"
                  data-testid="edit-featured-rank"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[11px] font-black uppercase tracking-widest">Label partenaire (badge)</Label>
                <Input
                  value={form.featured_partner || ''}
                  onChange={e => setForm({ ...form, featured_partner: e.target.value })}
                  placeholder="ex. Partenaire Code 2 Roues"
                  className="mt-1"
                  data-testid="edit-featured-partner"
                />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Rang <strong>1</strong> = tout en haut de la liste (partenaire principal). Laissez <strong>vide</strong> pour un affichage standard. Vous pouvez épingler <strong>plusieurs</strong> écoles avec le même rang (elles seront triées par label puis nom).
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving} data-testid="edit-save">
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormRow({ label, v, onChange, testid, className = '' }) {
  return (
    <div className={className}>
      <Label className="text-[11px] font-black uppercase tracking-widest">{label}</Label>
      <Input value={v || ''} onChange={e => onChange(e.target.value)} className="mt-1" data-testid={testid} />
    </div>
  );
}

/* ═══════════════ Dialog: création école ═══════════════ */

function CreateSchoolDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ nom: '', adresse: '', code_postal: '', ville: '', permis_str: 'A1,A2,A', site_internet: '', custom_phone: '' });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.nom.trim() || !form.ville.trim()) { toast.error('Nom et ville obligatoires.'); return; }
    setSaving(true);
    try {
      const permis = form.permis_str.split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
      await apiJson('/api/admin/annuaire/schools', 'POST', { ...form, permis });
      toast.success('Fiche créée.');
      onCreated();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="admin-annuaire-create-dialog">
        <DialogHeader>
          <DialogTitle>Nouvelle fiche moto-école</DialogTitle>
          <DialogDescription>Ajout manuel (source="admin"). Utile pour les écoles absentes de data.gouv.fr.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <FormRow label="Nom *" v={form.nom} onChange={v => setForm({ ...form, nom: v })} testid="create-nom" />
          <FormRow label="Adresse" v={form.adresse} onChange={v => setForm({ ...form, adresse: v })} testid="create-adresse" />
          <div className="grid grid-cols-3 gap-3">
            <FormRow label="CP" v={form.code_postal} onChange={v => setForm({ ...form, code_postal: v })} testid="create-cp" />
            <FormRow label="Ville *" v={form.ville} onChange={v => setForm({ ...form, ville: v })} testid="create-ville" className="col-span-2" />
          </div>
          <FormRow label="Permis" v={form.permis_str} onChange={v => setForm({ ...form, permis_str: v })} testid="create-permis" />
          <FormRow label="Téléphone" v={form.custom_phone} onChange={v => setForm({ ...form, custom_phone: v })} testid="create-tel" />
          <FormRow label="Site internet" v={form.site_internet} onChange={v => setForm({ ...form, site_internet: v })} testid="create-site" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving} data-testid="create-save">{saving ? 'Création…' : 'Créer'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════ Dialog: import XLSX ═══════════════ */

function ImportXlsxDialog({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    if (!file) { toast.error('Sélectionnez un fichier .xlsx'); return; }
    setUploading(true);
    try {
      const j = await apiUpload('/api/admin/annuaire/import-editorial', file);
      setResult(j);
      toast.success(`Import réussi : ${j.inserted} insérée(s), ${j.deduplicated_with_data_gouv} déduplication(s).`);
    } catch (e) { toast.error(e.message); }
    setUploading(false);
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="admin-annuaire-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5" /> Import éditorial (XLSX)</DialogTitle>
          <DialogDescription>
            Format attendu : 1 feuille, entêtes ligne 1 : Établissement · Adresse · Code postal · Ville · Département · Téléphone · Qualification moto · Type · A1 · A2 · A · AM · Source · Date vérification · Note.
          </DialogDescription>
        </DialogHeader>
        {!result ? (
          <div className="space-y-3">
            <input type="file" accept=".xlsx" onChange={e => setFile(e.target.files?.[0] || null)} className="w-full text-sm" data-testid="import-file" />
            <p className="text-[11.5px] text-muted-foreground">
              Dédup automatique : les écoles déjà présentes dans data.gouv.fr (match nom + CP) seront ignorées. Géocodage automatique via api-adresse.data.gouv.fr.
            </p>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <p className="text-emerald-600 font-bold">✓ Import terminé — feuille « {result.sheet} »</p>
            <ul className="text-[13px] space-y-0.5">
              <li>{result.inserted} nouvelle(s) fiche(s) insérée(s)</li>
              <li>{result.updated} fiche(s) mise(s) à jour</li>
              <li>{result.deduplicated_with_data_gouv} fiche(s) dédupliquée(s) avec data.gouv.fr</li>
              <li>{result.geocoded} fiche(s) géocodée(s)</li>
            </ul>
          </div>
        )}
        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={onClose}>Annuler</Button>
              <Button onClick={submit} disabled={uploading || !file} data-testid="import-submit">
                {uploading ? 'Import…' : 'Lancer l\'import'}
              </Button>
            </>
          ) : (
            <Button onClick={onImported}>Fermer</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════ Autres onglets ═══════════════ */

function SimpleListTab({ endpoint, columns, testidPrefix, emptyLabel }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiGet(endpoint).then(j => { setItems(j.items || []); setLoading(false); }).catch(e => { toast.error(e.message); setLoading(false); });
  }, [endpoint]);
  return (
    <div className="rounded-2xl border bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-[11px] font-black uppercase tracking-widest">
            <tr>{columns.map(c => <th key={c.key} className="text-left p-3">{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-t hover:bg-muted/20" data-testid={`${testidPrefix}-${i}`}>
                {columns.map(c => <td key={c.key} className="p-3 align-top">{c.render ? c.render(it) : (it[c.key] || '—')}</td>)}
              </tr>
            ))}
            {items.length === 0 && !loading && <tr><td colSpan={columns.length} className="p-8 text-center text-muted-foreground">{emptyLabel}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ClaimsTab() {
  return <SimpleListTab
    endpoint="/api/annuaire/admin/claims"
    testidPrefix="admin-claim-row"
    emptyLabel="Aucune revendication."
    columns={[
      { key: 'school_id', label: 'École (ID)', render: it => <span className="font-mono text-[11px]">{it.school_id}</span> },
      { key: 'email', label: 'Email' },
      { key: 'siret', label: 'SIRET' },
      { key: 'contact_name', label: 'Contact' },
      { key: 'status', label: 'Statut', render: it => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${it.status === 'verified' ? 'bg-emerald-500/15 text-emerald-600' : 'bg-amber-500/15 text-amber-600'}`}>
          {it.status === 'verified' ? 'Vérifiée' : 'OTP envoyé'}
        </span>
      ) },
      { key: 'updated_at', label: 'Mise à jour', render: it => formatDate(it.updated_at || it.created_at) },
    ]}
  />;
}

function OppositionsTab() {
  return <SimpleListTab
    endpoint="/api/annuaire/admin/oppositions"
    testidPrefix="admin-opposition-row"
    emptyLabel="Aucune opposition RGPD en attente."
    columns={[
      { key: 'school_id', label: 'École (ID)', render: it => <span className="font-mono text-[11px]">{it.school_id}</span> },
      { key: 'email', label: 'Email' },
      { key: 'reason', label: 'Motif', render: it => <div className="max-w-md line-clamp-3">{it.reason}</div> },
      { key: 'status', label: 'Statut', render: it => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${it.status === 'confirmed' ? 'bg-red-500/15 text-red-600' : 'bg-amber-500/15 text-amber-600'}`}>
          {it.status === 'confirmed' ? 'Confirmée (retirée)' : 'En attente'}
        </span>
      ) },
      { key: 'created_at', label: 'Créée', render: it => formatDate(it.created_at) },
    ]}
  />;
}

function ReportsTab() {
  return <SimpleListTab
    endpoint="/api/annuaire/admin/reports"
    testidPrefix="admin-report-row"
    emptyLabel="Aucun signalement."
    columns={[
      { key: 'school_id', label: 'École (ID)', render: it => <span className="font-mono text-[11px]">{it.school_id}</span> },
      { key: 'field', label: 'Champ' },
      { key: 'message', label: 'Message', render: it => <div className="max-w-md line-clamp-3">{it.message}</div> },
      { key: 'reporter_email', label: 'Email signalant' },
      { key: 'created_at', label: 'Créée', render: it => formatDate(it.created_at) },
    ]}
  />;
}

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return '—'; }
}

/* ═══════════════ Sync data.gouv.fr (bouton admin) ═══════════════ */
function BulkFeatureButton({ onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name_pattern: '', departements: '', rank: 1, partner_label: 'Partenaire Code 2 Roues', unset: false });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (!form.name_pattern.trim()) { toast.error('Nom (regex) requis.'); return; }
    setBusy(true);
    try {
      const body = {
        name_pattern: form.name_pattern.trim(),
        departements: form.departements.split(',').map(s => s.trim()).filter(Boolean),
        rank: Number(form.rank) || 1,
        partner_label: form.partner_label || null,
        unset: form.unset,
      };
      const j = await apiJson('/api/admin/annuaire/feature-by-name', 'POST', body);
      setResult(j);
      toast.success(form.unset ? `Retiré ${j.modified} fiches` : `${j.modified} fiche(s) épinglée(s) rang ${j.rank}.`);
      onDone && onDone();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => { setResult(null); setOpen(true); }} className="border-amber-500/40 text-amber-600 hover:bg-amber-500/10" data-testid="admin-annuaire-bulk-feature-btn">
        <Award className="w-3.5 h-3.5 mr-1.5" /> Mettre en avant (bulk)
      </Button>
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent data-testid="admin-annuaire-bulk-feature-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Award className="w-5 h-5 text-amber-500" /> Mise en avant en masse</DialogTitle>
            <DialogDescription>Épinglez d'un coup toutes les fiches d'une chaîne de moto-écoles sur un ou plusieurs départements.</DialogDescription>
          </DialogHeader>
          {!result ? (
            <div className="space-y-3">
              <FormRow label="Nom (regex tolérant, insensible à la casse)" v={form.name_pattern} onChange={v => setForm({ ...form, name_pattern: v })} testid="bulk-name" />
              <FormRow label="Départements (codes séparés par virgule, ex: 38,69)" v={form.departements} onChange={v => setForm({ ...form, departements: v })} testid="bulk-depts" />
              <div className="grid grid-cols-2 gap-3">
                <FormRow label="Rang (1 = tout en haut)" v={form.rank} onChange={v => setForm({ ...form, rank: v })} testid="bulk-rank" />
                <FormRow label="Label partenaire (badge)" v={form.partner_label} onChange={v => setForm({ ...form, partner_label: v })} testid="bulk-partner" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.unset} onCheckedChange={v => setForm({ ...form, unset: !!v })} data-testid="bulk-unset" />
                <span>Au contraire : <strong>retirer</strong> la mise en avant de ces fiches</span>
              </label>
            </div>
          ) : (
            <div className="text-sm space-y-2">
              <p className="text-emerald-600 font-bold">✓ {result.modified} fiche(s) mise(s) à jour</p>
              <ul className="text-[12px] space-y-0.5 max-h-64 overflow-auto pr-2">
                {(result.preview || []).map(p => (
                  <li key={p.id}>[{p.departement}] {p.nom} — {p.ville}</li>
                ))}
              </ul>
            </div>
          )}
          <DialogFooter>
            {!result ? (
              <>
                <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Annuler</Button>
                <Button onClick={run} disabled={busy} data-testid="admin-annuaire-bulk-feature-submit">
                  {busy ? 'En cours…' : form.unset ? 'Retirer la mise en avant' : 'Épingler'}
                </Button>
              </>
            ) : (
              <Button onClick={() => { setOpen(false); setResult(null); }}>Fermer</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ═══════════════ Sync data.gouv.fr (bouton admin) ═══════════════ */
function SyncDataGouvButton({ onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState('');
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      setStep('Import depuis data.gouv.fr (Sécurité Routière)…');
      const j1 = await apiJson('/api/admin/annuaire/refresh', 'POST');
      setStep('Géocodage batch (api-adresse.data.gouv.fr)…');
      const j2 = await apiJson('/api/admin/annuaire/geocode', 'POST');
      setStep('');
      setResult({ import: j1, geocode: j2 });
      toast.success(`Sync OK : ${j1.imported} écoles importées, ${j2.geocoded} géocodées.`);
      onDone && onDone();
    } catch (e) {
      setStep('');
      toast.error(e.message || 'Erreur sync');
    }
    setBusy(false);
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="admin-annuaire-sync-btn" className="border-emerald-500/40 text-emerald-600 hover:bg-emerald-500/10">
        <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Sync data.gouv.fr
      </Button>
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent data-testid="admin-annuaire-sync-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><RefreshCw className="w-5 h-5 text-emerald-600" /> Synchronisation data.gouv.fr</DialogTitle>
            <DialogDescription>
              Cette opération télécharge la <strong>liste officielle des moto-écoles agréées</strong> (Sécurité Routière) puis géocode les adresses.
              Durée : environ <strong>1 à 3 minutes</strong>. À faire notamment au premier déploiement ou après une longue période sans sync (le cron nightly reprend ensuite le relais).
            </DialogDescription>
          </DialogHeader>
          {!result ? (
            busy ? (
              <div className="py-4 text-center space-y-2">
                <RefreshCw className="w-6 h-6 mx-auto animate-spin text-emerald-600" />
                <p className="text-sm text-muted-foreground">{step}</p>
                <p className="text-[11px] text-muted-foreground">Ne fermez pas cette fenêtre.</p>
              </div>
            ) : (
              <div className="text-sm space-y-2">
                <p>La base actuelle sera <strong>remplacée</strong> par la version la plus récente de data.gouv.fr.</p>
                <p className="text-[12px] text-muted-foreground">Les fiches éditoriales (source=editorial) et les revendications sont conservées.</p>
              </div>
            )
          ) : (
            <div className="text-sm space-y-2">
              <p className="text-emerald-600 font-bold">✓ Synchronisation terminée</p>
              <ul className="space-y-0.5 text-[13px]">
                <li><strong>{result.import.imported}</strong> moto-écoles importées ({result.import.skipped_non_moto} lignes non-moto ignorées)</li>
                <li><strong>{result.geocode.geocoded}</strong> adresses géocodées sur {result.geocode.attempted}</li>
              </ul>
            </div>
          )}
          <DialogFooter>
            {!result ? (
              <>
                <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Annuler</Button>
                <Button onClick={run} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500" data-testid="admin-annuaire-sync-confirm">
                  {busy ? 'En cours…' : 'Lancer la synchro'}
                </Button>
              </>
            ) : (
              <Button onClick={() => { setOpen(false); setResult(null); }}>Fermer</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
